---
title: "The best Trustpilot APIs in 2026, priced per 1,000 reviews"
description: "Six ways to pull Trustpilot reviews for companies you don't own, tested on 20 brands and priced per 1,000 reviews. Which to use for monitoring or full history."
date: "2026-10-16"
postType: "alternatives"
author: "Kevin Wang"
tags: ["trustpilot", "reviews", "api", "web-scraping", "comparison"]
keyword: "trustpilot api"
ogImage: "/og/trustpilot-api.png"
---

export const faqItems = [
  {
    q: "Does Trustpilot have a public API?",
    a: "Only for its own business customers. Trustpilot's API module is an add-on to a Trustpilot for Business plan, which starts at $99 a month billed annually with a 12-month commitment (business.trustpilot.com/pricing, September 30, 2026), and it reads your own company's data, not other companies' reviews.",
  },
  {
    q: "How many Trustpilot reviews can an API get for one company?",
    a: "Usually 200, the most Trustpilot's website shows signed out. On September 30, 2026, asking for page 11 of the reviews sent a signed-out browser to the login page for all 20 companies tested, and AnyAPI and DataForSEO both stop at 200. Apify's Trustpilot scrapers say they go past it; that claim wasn't tested here.",
  },
  {
    q: "How much does a Trustpilot reviews API cost per 1,000 reviews?",
    a: ["On September 30, 2026: DataForSEO's standard queue was $0.0375 per 1,000 reviews, Apify's Trustpilot Reviews Scraper $0.575 on the Free plan, and AnyAPI ", { sku: "trustpilot.reviews", items: 20 }, " at 20 reviews a call. Trustpilot doesn't publish a price for its own API."],
  },
  {
    q: "How often should you poll Trustpilot for new reviews?",
    a: "Hourly for big brands. On September 30, 2026, the newest 20 reviews covered 24 minutes for Klarna and less than a day for 10 of the 20 companies tested, so a daily poll that asks for 20 reviews misses most of them.",
  },
];

export const pricePer1k = [
  { label: "DataForSEO queued", value: 0.0375 },
  { label: "AnyAPI, 20 per call", sku: "trustpilot.reviews", items: 20 },
  { label: "DataForSEO priority", value: 0.075 },
  { label: "Apify scraper, Free plan", value: 0.575 },
  { label: "AnyAPI backup, 200 per call", sku: "trustpilot.reviews", failover: true, items: 200 },
];

<Callout type="tip" title="Key findings">

- **If the reviews aren't yours, skip Trustpilot's own API.** It's an add-on to a Trustpilot for Business contract, and it returns your company's data, not your competitor's. Third-party APIs read any company's public reviews, and on September 30, 2026 the cheapest listed price was [DataForSEO's](https://dataforseo.com/apis/business-data-api/trustpilot-api) $0.0375 per 1,000 reviews, queued.
- **To watch for new reviews, ask for 20 at a time.** [AnyAPI](https://getanyapi.com/api/trustpilot) returned the newest 20 on all 20 companies I tried, at <Price sku="trustpilot.reviews" items={20} /> per 1,000 reviews.
- **Leave out the limit and you ask for 200, and the bill can jump about 20 times.** On the day I tested, every 200-review call was served by the backup source.
- **Signed out, Trustpilot shows 200 reviews per company and then asks you to log in.** For anything older, [Apify's Trustpilot scrapers](https://apify.com/automation-lab/trustpilot) are the only option here that says it goes further.

</Callout>

Maybe you run support and want a Slack ping when a competitor collects a wave of one-star reviews. Maybe you're building a sentiment dashboard and need the review text for fifty brands, every morning.

Either way, you need Trustpilot reviews for companies you don't own, and Trustpilot doesn't sell that. I sent the same 20 companies, from Klarna to Zalando, through AnyAPI and through Trustpilot's own pages in a headless browser, and read the pricing pages and docs for the rest.

The price per 1,000 reviews ranges from a few cents to nearly a dollar, and the setting that decides which end you pay is easy to miss.

## The best Trustpilot APIs

- [Trustpilot API](#best-trustpilot-api-for-your-own-companys-reviews) for your own company's reviews
- [AnyAPI](#best-trustpilot-api-for-monitoring-new-reviews) for monitoring new reviews
- [DataForSEO](#best-trustpilot-api-for-the-lowest-price-when-you-can-wait) for the lowest price when you can wait
- [Apify Trustpilot Reviews Scraper](#best-trustpilot-api-for-full-review-history-past-200) for full review history past 200
- [OpenWeb Ninja Trustpilot API](#best-trustpilot-api-for-a-monthly-plan-with-a-free-tier) for a monthly plan with a free tier
- [Trustpilot pages in a headless browser](#best-trustpilot-api-for-zero-api-fees-if-you-run-the-browser-yourself) for zero API fees, if you run the browser yourself

## What makes a good Trustpilot API?

A good Trustpilot API reads public reviews of companies you don't own, from code. I'm assuming that's your job: a list of domains, a job that runs on a schedule, and a table the reviews go into. Here's what I compared on.

- **Access without owning the company.** A route that only reads your own business unit can't do competitor research.
- **Price per 1,000 reviews.** Some vendors bill per call, some per review, some per 20 reviews. I turned each into what 1,000 reviews cost.
- **How far back you can go.** The newest 20 reviews is one job. The last two years is another, and most routes stop at 200.
- **Fields on every review.** Rating, title, text, date, and review URL are the floor. Reviewer country, the company's reply, and a verified flag decide what you can filter on.

<Callout type="note" title="How this was tested, and its limits">

- AnyAPI: 20 calls asking for 20 reviews each, one per company, and 9 calls asking for the default 200 before I stopped, all on 2026-09-30. That shows the response shape, the rough speed, and which source served. It doesn't show uptime.
- Trustpilot's pages: page 1, page 10, and page 11 for each of the same 20 companies, loaded in headless Chrome on the same day.
- DataForSEO, Apify, OpenWeb Ninja, and Trustpilot's official API were not run. DataForSEO's minimum payment is $50, the others need an account I didn't open, and the official API needs a Trustpilot for Business contract. Their sections are page reads.
- I didn't compare review text against the website, or test filters by star, language, or date.
- Prices are per 1,000 reviews at the batch size named in each row, checked on 2026-09-30. Monthly plans assume you use the whole plan.

</Callout>

## The best Trustpilot APIs at a glance

| Tool | Best for | Why pick it | Price per 1,000 reviews | Tested on 2026-09-30 |
| --- | --- | --- | --- | --- |
| Trustpilot API | Your own company's reviews | Official, up to 100 reviews a call | Not published; an add-on to a 12-month business plan | Not run; needs a business contract |
| AnyAPI | Monitoring new reviews | Newest 20 in about 5 seconds, no plan | <Price sku="trustpilot.reviews" items={20} /> at 20 per call; <Price sku="trustpilot.reviews" failover items={200} /> when the backup source serves a 200-review call | Newest 20 for 20 of 20 companies; all 9 calls for 200 reviews served by the backup |
| DataForSEO | The lowest price when you can wait | $37.50 per million reviews queued | $0.0375 queued (up to 45 minutes), $0.075 priority; $50 minimum payment | Not run; pricing page and docs |
| Apify Trustpilot Reviews Scraper | Full review history past 200 | Says it pages through thousands of reviews | $0.575 plus $0.005 a run on the Free plan | Not run; Apify Store page |
| OpenWeb Ninja Trustpilot API | A monthly plan with a free tier | 100 free requests a month | $25 a month for 10,000 requests; reviews per request not listed | Not run; RapidAPI listing |
| Trustpilot pages in a headless browser | Zero API fees | Same 20 reviews a page as the website | $0 API fee, plus your own browser and servers | Page 1 read for 20 of 20; page 11 went to login for 20 of 20 |

<Chart caption={<>What 1,000 reviews cost. AnyAPI's bars are the catalog's current prices at 20 and 200 reviews a call; the backup bar is what you pay when the backup source serves. DataForSEO and Apify are from their pricing pages on 2026-09-30, with Apify's run fee left out.</>}>
  <BarChart data={pricePer1k} showValues valueSuffix="" baseline={0} height={260} format={(n) => `$${n < 0.1 ? n.toFixed(4) : n.toFixed(3)}`} ariaLabel="Price per 1,000 Trustpilot reviews" />
</Chart>

## Best Trustpilot API for your own company's reviews

### Trustpilot API

**Trustpilot API pros:**

- The official source, with invitation links and product reviews alongside service reviews
- Up to 100 service reviews per call, five times a page of the website

**Trustpilot API cons:**

- Only for Trustpilot for Business customers, and it reads your own business data
- The price isn't published

Trustpilot's official API is the answer if Trustpilot collects reviews for your company and you want them in your own database. The [API module](https://developers.trustpilot.com/api-module-overview) is an add-on for Trustpilot for Business users, sold in three tiers of 200,000, 1,000,000, and unlimited calls a year, and a service reviews call returns up to 100 reviews.

The official API is the wrong tool for anything else. Trustpilot's docs say the module "gives you access to your business data", and its [business pricing page](https://business.trustpilot.com/pricing) lists API access as an add-on, on plans that start at $99 a month, billed annually, with a 12-month commitment. There's no self-serve key for reading another company's page.

**Trustpilot API pricing:** not published. Business plans start at $99 a month billed annually; the API module is an add-on, and the Enterprise plan includes its first tier. Source: [business pricing page](https://business.trustpilot.com/pricing), September 30, 2026.

## Best Trustpilot API for monitoring new reviews

### AnyAPI

**AnyAPI pros:**

- The newest 20 reviews on all 20 companies, newest first, at <Price sku="trustpilot.reviews" per="call" /> a call
- Title, text, date, URL, reviewer name, country, language, and review count on every one of the 400 reviews
- The company's reply when there is one, with its own timestamp
- One POST with a domain; no plan, and the price is quoted before you call

**AnyAPI cons:**

- No cursor, so the newest 200 is as deep as it goes
- A 200-review call on the day I tested was served by the backup source every time, at about 20 times the per-review price and about a minute per call

AnyAPI's Trustpilot reviews endpoint is the pick if your job is "tell me when a company gets new reviews". You send a domain and a limit, and the reviews come back as the same JSON whatever company you ask about.

```bash
curl -sS -X POST 'https://api.getanyapi.com/v1/run/trustpilot.reviews' \
  -H "Authorization: Bearer $ANYAPI_API_KEY" \
  -H 'Content-Type: application/json' \
  -d '{"company":"nordvpn.com","limit":20}'
```

Here's one of the 20 reviews it returned on 2026-09-30, with the rest of the envelope:

```json
{
  "output": {
    "found": true,
    "data": {
      "items": [
        {
          "author": "NordVPN Customer",
          "authorCountry": "DZ",
          "createdUtc": 1790775532,
          "language": "en",
          "rating": 5,
          "reviewerReviewsCount": 1,
          "title": "best vpn ever tried",
          "text": "best vpn ever tried",
          "url": "https://www.trustpilot.com/reviews/6abcf4cc61e99b491a34ead0",
          "verified": true,
          "ownerResponseText": "Hello. Thank you for this wonderful review! Stay safe. -L.L.",
          "ownerResponseUtc": 1790778844
        }
      ]
    }
  },
  "costUsd": 0.0008,
  "items": 20,
  "source": { "name": "Quokka", "kind": "anonymous" }
}
```

All 20 calls came back from the same source at $0.0008 each, with a median of about 5 seconds and a slowest of about 10. {/* receipt 2026-09-30 */} The reviews span 12 languages, and only about 63% were in English, so filter on `language` if your dashboard is English-only.

**Twenty reviews is a lot for a small company and almost nothing for a big one**, so how often you poll matters.

<Figure src="/og/charts/trustpilot-api-review-span.svg" width={760} height={940} caption="How far back the newest 20 reviews reached, per company, from one limit-20 call each on September 30, 2026." alt="How far back the newest 20 Trustpilot reviews reach, per company, on 2026-09-30: Klarna 24 minutes, Netflix and Revolut 42 minutes, HelloFresh 3.7 hours, Wise 3.8 hours, Booking.com 4.9 hours, Monzo 6.9 hours, PayPal 9.5 hours, NordVPN 10.6 hours, Amazon 11.9 hours, Uber 25.9 hours, Gymshark 26.1 hours, IKEA 32.6 hours, Etsy 33.1 hours, Airbnb 2.9 days, Shopify 5.8 days, Chewy 7 days, Stripe 8.3 days, Spotify 9.2 days, Zalando 91.2 days" />

| Newest 20 reviews reached back | Companies, 2026-09-30 |
| --- | --- |
| Under an hour | Klarna 24 minutes, Netflix 42 minutes, Revolut 42 minutes |
| 1 to 24 hours | HelloFresh 3.7 h, Wise 3.8 h, Booking.com 4.9 h, Monzo 6.9 h, PayPal 9.5 h, NordVPN 10.6 h, Amazon 11.9 h |
| 1 to 7 days | Uber 25.9 h, Gymshark 26.1 h, IKEA 32.6 h, Etsy 33.1 h, Airbnb 2.9 days, Shopify 5.8 days, Chewy 7 days |
| Over a week | Stripe 8.3 days, Spotify 9.2 days, Zalando 91.2 days |

For half the companies, 10 of 20, the newest 20 reviews covered less than a day. Klarna's covered 24 minutes. If you poll Klarna once a day with a limit of 20, you'll see the last half hour and miss the rest. Poll the big brands hourly instead; at <Price sku="trustpilot.reviews" per="call" /> a call that's <Price sku="trustpilot.reviews" calls={720} /> a month per brand.

Now the default. Leave out `limit` and the API assumes you want 200, the most Trustpilot shows signed out. That call is quoted differently, and the quote endpoint tells you so before you spend anything:

```bash
curl -sS -X POST 'https://api.getanyapi.com/v1/apis/trustpilot.reviews/quote' \
  -H "Authorization: Bearer $ANYAPI_API_KEY" \
  -H 'Content-Type: application/json' \
  -d '{"company":"stripe.com"}'
# 2026-09-30, no limit (200 reviews):
# {"minCostUsd":0.005,"maxCostUsd":0.165,"pricing":"Flat $0.005 per call."}
# 2026-09-30, "limit":20:
# {"minCostUsd":0.0008,"maxCostUsd":0.0462,"pricing":"Flat $0.0008 per call."}
```

The low figure is the first source that can serve the call, and the high one is the backup. **On 2026-09-30, all nine of my 200-review calls were served by the backup and billed $0.165 each.** {/* receipt 2026-09-30 */} Each returned the full 200 reviews, so that's $0.825 per 1,000 reviews against $0.04 at 20 a call, and they took about a minute each, one of them 105 seconds. {/* receipt 2026-09-30 */} I stopped after nine rather than spend more to confirm the pattern.

The fallback did its job: every call came back complete. But if you only need new reviews, send `"limit": 20` and the price stays at the low end. Filters by language, country, or start date also route past the cheap source, and the input schema says so field by field.

**AnyAPI pricing:** <Price sku="trustpilot.reviews" per="call" /> a call for up to 20 reviews, which is <Price sku="trustpilot.reviews" items={20} /> per 1,000 reviews. A call the backup source serves costs up to <Price sku="trustpilot.reviews" failover per="call" />, which is <Price sku="trustpilot.reviews" failover items={200} /> per 1,000 at 200 reviews a call. Prepaid, no plan; new accounts get <Price sku="trustpilot.reviews" budget={0.1} /> calls of free credit.

## Best Trustpilot API for the lowest price when you can wait

### DataForSEO

<Figure src="/og/screenshots/trustpilot-api-dataforseo-pricing.png" alt="DataForSEO's Trustpilot Reviews pricing: standard queue up to 45 minutes at $0.00075 per 20 reviews or $37.5 per million, high priority up to 1 minute at $0.0015 per 20 reviews or $75 per million" width={1280} height={900} />

**DataForSEO pros:**

- $0.0375 per 1,000 reviews on the standard queue, less per review than any call I actually made
- Reads any business listed on Trustpilot, with a Trustpilot search endpoint beside it

**DataForSEO cons:**

- Queued only: up to 45 minutes on the standard queue, up to a minute on priority at double the price
- $50 minimum payment
- Billed per 20 reviews, so asking for 21 costs the same as 40

[DataForSEO](https://dataforseo.com/apis/business-data-api/trustpilot-api) is the cheapest way to fill a table overnight, at $0.0375 per 1,000 reviews on its standard queue. You post a task with a domain and a depth, then come back for the result. Its docs cap depth at 200, the same number the website shows signed out.

The standard queue costs a little less per review than AnyAPI's 20-review calls and can take most of an hour. Priority costs twice as much and answers within a minute. If your job runs at 3am and nobody's waiting, the standard queue is the pick. If a person clicked a button, it isn't.

I didn't run it. DataForSEO's minimum payment is $50, so this section is from its pricing page and docs.

**DataForSEO pricing:** $0.00075 per 20 reviews queued ($37.50 per million), $0.0015 per 20 with priority ($75 per million). $50 minimum payment. Source: [pricing page](https://dataforseo.com/apis/business-data-api/trustpilot-api), September 30, 2026.

## Best Trustpilot API for full review history past 200

### Apify Trustpilot Reviews Scraper

<Figure src="/og/screenshots/trustpilot-api-apify-pricing.png" alt="The Apify Store page for automation-lab's Trustpilot Reviews Scraper: pricing from $0.30 per 1,000 reviews, a $0.005 run start fee, 2.3K total users, and a README saying it can scrape tens of thousands of reviews from one company in a run" width={1280} height={900} />

**Apify Trustpilot Reviews Scraper pros:**

- Says it pages past the 200 reviews the signed-out website shows
- Filters by stars, language, date range, verified, and replies
- Apify's Free plan includes $5 a month to spend, no card required

**Apify Trustpilot Reviews Scraper cons:**

- A run is a batch job: its own example puts 2,748 reviews at about 45 minutes
- $0.575 per 1,000 reviews on the Free plan, well above AnyAPI and DataForSEO

Automation Lab's [Trustpilot Reviews Scraper](https://apify.com/automation-lab/trustpilot) on Apify is the one to try if you need a company's whole history, not its last 200 reviews. It's one of the two most-used Trustpilot actors on the Apify Store by users over the last 30 days (Apify Store, September 30, 2026), and its README says it loads Trustpilot pages in a headless browser and "can scrape tens of thousands of reviews from a single company in one run".

Its pricing is a $0.005 start fee per run plus a charge per review that falls with your Apify plan, from $0.000575 on Free to $0.00014 on Diamond. Its own examples put 200 reviews at about $0.12 and 2,748 at about $1.59.

The other popular actor, memo23's [Trustpilot All Reviews Scraper](https://apify.com/memo23/trustpilot-scraper-ppe), makes the same promise a different way: it says it reads Trustpilot's mobile app endpoints to get "past Trustpilot's ~200-review web cap". It charges $0.00075 a review and a start fee from $0.05 a run on the Free plan.

I didn't run either, so treat the depth claims as theirs. If you only need recent reviews, AnyAPI and DataForSEO cost less per review.

**Apify Trustpilot Reviews Scraper pricing:** $0.005 per run plus $0.000575 per review on Apify's Free plan, down to $0.00014 per review on Diamond. The Free plan includes $5 a month. Source: [Apify Store page](https://apify.com/automation-lab/trustpilot), September 30, 2026.

## Best Trustpilot API for a monthly plan with a free tier

### OpenWeb Ninja Trustpilot API

**OpenWeb Ninja Trustpilot API pros:**

- 100 free requests a month, no card required
- Company search, category listings, and reviewer profiles beside company reviews
- Filters on verified, date, and rating, like the website's review page

**OpenWeb Ninja Trustpilot API cons:**

- Sold by the request, and the listing doesn't say how many reviews a request returns
- Monthly plans through RapidAPI, with a bandwidth fee past 10 GB

[OpenWeb Ninja's Trustpilot Company and Reviews Data](https://rapidapi.com/letscrape-6bRBa3QguO5/api/trustpilot-company-and-reviews-data) covers more of Trustpilot than a reviews endpoint: it searches companies, lists categories, and reads reviewer profiles as well as reviews. If your product needs to find companies on Trustpilot, not just read ones you already know, start here.

I can't tell you its price per 1,000 reviews. The plans are sold per request, from $25 a month for 10,000 up to $150 for 200,000, and the listing doesn't state how many reviews come back per request. The free plan's 100 requests is enough to find out before you pay.

**OpenWeb Ninja Trustpilot API pricing:** 100 requests a month free, $25 a month for 10,000, $75 for 50,000, $150 for 200,000, with overage from $0.003 to $0.001 a request. Source: [RapidAPI listing](https://rapidapi.com/letscrape-6bRBa3QguO5/api/trustpilot-company-and-reviews-data), September 30, 2026.

## Best Trustpilot API for zero API fees, if you run the browser yourself

### Trustpilot pages in a headless browser

<Figure src="/og/screenshots/trustpilot-api-page-11-login.png" alt="Trustpilot's log in or sign up page, which a signed-out browser reaches when it asks for page 11 of a company's reviews" width={1280} height={900} />

**Trustpilot pages in a headless browser pros:**

- $0 in API fees
- Every review page carries its reviews as JSON in the page, 20 to a page
- About 1.2 seconds a page once the browser is past the bot check

**Trustpilot pages in a headless browser cons:**

- A plain HTTP request gets a 403 "Verifying your connection" page
- Page 11 sends you to a login page, so 200 reviews is the ceiling
- You own the browser, the servers, and the day the page changes

Trustpilot's own review pages carry each review as JSON with the same fields the paid APIs return, which makes it tempting to skip the API entirely.

The first wall is a bot check. A plain `curl` of a review page got a 403 and a "Verifying your connection" page. Headless Chrome got through after a short wait, then read page 1 of all 20 companies, 20 reviews each, in a median of about 1.2 seconds a page.

The second wall is harder. **For all 20 companies, asking for page 11 redirected to Trustpilot's login page.** Ten pages of 20 is 200 reviews, the same ceiling AnyAPI and DataForSEO stop at. Even 200 isn't much for a big brand: the 200 newest Booking.com reviews reached back about two and a half days.

This works for a side project with a few companies. For a job that runs every hour across hundreds, you're now running browsers to save a fraction of a cent per call.

**Trustpilot pages in a headless browser pricing:** $0 API fee. Your own compute, and your own maintenance when the bot check changes.

## Which Trustpilot API should you use?

The right Trustpilot API depends on whose reviews you need and how far back you need them.

- You want your own company's reviews in your own systems: Trustpilot API.
- You want new reviews for other companies as they arrive: AnyAPI with `"limit": 20`, polled hourly for the big brands.
- You're filling a table overnight and nobody's waiting: DataForSEO's standard queue.
- You need more than the newest 200 reviews for a company: an Apify Trustpilot scraper, and check its depth on your first run.
- You also need to search Trustpilot for companies: OpenWeb Ninja, starting on the free plan.
- You're reading a handful of companies and you're happy to run a browser: Trustpilot's own pages.

If you're weighing scraping platforms more broadly, the [Apify alternatives comparison](/blog/apify-alternatives) covers that, and the [API catalog](/catalog) lists the other review sources you can call with the same key.

<FAQ items={faqItems} />

<CTA to="/api/trustpilot/reviews" note={<>Trustpilot reviews as clean JSON, <Price sku="trustpilot.reviews" per="call" /> for the newest 20, no plan and nothing that expires. New accounts get free credit, no card required.</>}>
  Try the Trustpilot Reviews API
</CTA>

Use data responsibly and follow AnyAPI's [Acceptable Use Policy](/acceptable-use).
